import apiReq from 'core/api';
import type { ContextFile } from 'types';

export interface LlmModel {
  id: string;
  name: string;
}

export interface BackendRole {
  id: string;
  name: string;
  title?: string | null;
}

export type BackendScenarioRole = string | BackendRole | null;

export interface BackendScenario {
  id: string;
  role?: BackendRole;
  title: string;
  description: string | null;
  action: string | null;
  result: string | null;
  instruction: string | null;
  system_prompt: string | null;
  min_files: number | null;
  max_files: number | null;
  model: LlmModel | null;
  ordering: number;
  created_at: number;
  updated_at: number;
  context_documents: ContextFile[];
}

export const getScenarioRoleId = (scenario: BackendScenario): string => {
  const role = scenario.role?.id;
  return typeof role === 'string' ? role : (role ?? '');
};

export const getScenarioRoleName = (scenario: BackendScenario): string | null => {
  const role = scenario.role?.name;
  return typeof role === 'string' ? null : (role ?? null);
};

export interface ScenarioSavePayload {
  role_id: string;
  title: string;
  description?: string;
  action?: string;
  result?: string;
  instruction?: string;
  system_prompt?: string;
  min_files?: number;
  max_files?: number;
  model?: string | null;
  ordering?: number;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export const listScenarios = (
  roleId?: string
): Promise<{ count: number; items: BackendScenario[] } | undefined> => {
  const validRoleId = roleId && UUID_RE.test(roleId) ? roleId : undefined;
  const query = validRoleId
    ? `?role_id=${encodeURIComponent(validRoleId)}&limit=1000&offset=0`
    : '?limit=1000&offset=0';
  return apiReq<{ count: number; items: BackendScenario[] }>(`scenarios${query}`);
};

export const getScenario = (id: string): Promise<BackendScenario | undefined> =>
  apiReq<BackendScenario>(`scenario/${encodeURIComponent(id)}/view`);

export const createScenario = (data: ScenarioSavePayload): Promise<BackendScenario | undefined> =>
  apiReq<BackendScenario>('scenarios/create', 'POST', data);

export const updateScenario = (
  id: string,
  data: ScenarioSavePayload
): Promise<BackendScenario | undefined> =>
  apiReq<BackendScenario>(`scenario/${encodeURIComponent(id)}/update`, 'POST', data);

export const deleteScenario = (id: string): Promise<void> =>
  apiReq<void>(`scenario/${encodeURIComponent(id)}/delete`, 'DELETE').then(() => undefined);

// ─── Roles ────────────────────────────────────────────────────────────────────

export const listRoles = (): Promise<BackendRole[] | undefined> =>
  apiReq<BackendRole[]>('roles/all');

// ─── LLM Models ───────────────────────────────────────────────────────────────

export const listLlmModels = (): Promise<LlmModel[] | undefined> =>
  apiReq<LlmModel[]>('llm/info/models');
